"use client";
import React, { useState } from "react";
import axios from "axios";
import toast, { Toaster } from "react-hot-toast";
import { prodServerUrl } from "../utils/globals";
import WhatsAppButton from "@/components/common/WhatsAppButton";
import Link from "next/link";
import { 
  FaFacebookF, 
  FaYoutube, 
  FaLinkedinIn, 
  FaInstagram,
  FaMapMarkerAlt, 
  FaEnvelope, 
  FaPhoneAlt,
  FaPaperPlane 
} from "react-icons/fa";
import CustomHead from "@/utils/CustomHead";
import { useRouter } from "next/navigation";
import { motion } from "framer-motion";

const ContactPage = () => {
  const [disable, setDisable] = useState(false);
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    subject: "",
    message: ""
  });

  const router = useRouter();

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const { firstName, lastName, email, phone, subject, message } = formData;

    if (!firstName || !lastName || !email || !phone || !subject || !message) {
      toast.error('All Fields Are Necessary');
      return;
    }

    try {
      setDisable(true);
      const res = await axios.post(`${prodServerUrl}/contact-forms`, formData);

      if (res.status === 201) {
        toast.success("Message sent successfully!");
        setTimeout(() => router.push('/thank-you'), 1000);
        setFormData({
          firstName: "",
          lastName: "",
          email: "",
          phone: "",
          subject: "",
          message: ""
        });
      }
    } catch (error) {
      toast.error('An error occurred. Please try again.');
      console.log(error);
    } finally {
      setDisable(false);
    }
  };

  const socialLinks = [
    { icon: FaFacebookF, href: "https://www.facebook.com/vidyastueducation", color: "hover:bg-[#1877F2]" },
    { icon: FaYoutube, href: "https://www.youtube.com/@vidyastu1787", color: "hover:bg-[#FF0000]" },
    { icon: FaInstagram, href: "https://www.instagram.com/vidyastu_education/", color: "hover:bg-[#E4405F]" },
    { icon: FaLinkedinIn, href: "https://www.linkedin.com/company/vidyastu-education/", color: "hover:bg-[#0A66C2]" },
  ];

  return (
    <>
      <CustomHead 
        title="Contact Vidyastu | Get in Touch"
        description="Have questions? Contact Vidyastu for course details, support, and inquiries."
      />
      
      <Toaster position="top-center" />

      <main className="min-h-screen bg-slate-50 relative font-sans">
        
        {/* --- Hero Header Background --- */}
        <div className="bg-slate-900 h-[500px] w-full relative overflow-hidden">
          <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-20"></div>
          <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-blue-600/20 rounded-full blur-[120px] pointer-events-none"></div>
          <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-purple-600/20 rounded-full blur-[100px] pointer-events-none"></div>
          
          <div className="container mx-auto px-6 h-full flex flex-col justify-center items-center text-center relative z-10 pb-20">
            <motion.span 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="inline-block py-1 px-3 rounded-full bg-blue-500/10 border border-blue-500/20 text-blue-400 text-xs font-bold uppercase tracking-widest mb-4"
            >
              24/7 Support
            </motion.span>
            <motion.h1 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="text-4xl md:text-6xl font-bold text-white mb-6"
            >
              Let's Start a Conversation
            </motion.h1>
            <motion.p 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="text-lg text-slate-300 max-w-2xl"
            >
              Have questions about our courses or need career guidance? Our team is here to help you navigate your journey.
            </motion.p>
          </div>
        </div>

        {/* --- Main Content Overlap --- */}
        <div className="container mx-auto px-4 md:px-6 relative z-20 -mt-32 mb-20">
          <div className="bg-white rounded-3xl shadow-2xl overflow-hidden flex flex-col lg:flex-row">
            
            {/* Left: Contact Info (Dark Theme) */}
            <div className="lg:w-5/12 bg-slate-800 p-10 md:p-12 text-white relative overflow-hidden">
              {/* Decor */}
              <div className="absolute top-0 right-0 w-64 h-64 bg-blue-500/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2"></div>
              
              <div className="relative z-10 h-full flex flex-col justify-between">
                <div>
                  <h3 className="text-2xl text-yellow font-bold mb-2">Contact Information</h3>
                  <p className="text-slate-400 text-sm mb-10">Say something to start a live chat!</p>

                  <div className="space-y-8">
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center text-blue-400 flex-shrink-0">
                        <FaPhoneAlt />
                      </div>
                      <div>
                        <p className="text-xs text-slate-400 uppercase font-bold tracking-wider mb-1">Call Us</p>
                        <a href="tel:+917450010203" className="block hover:text-blue-400 transition-colors">+91 74500 10203</a>
                        <a href="tel:+919874788747" className="block hover:text-blue-400 transition-colors">+91 98747 88747</a>
                      </div>
                    </div>

                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center text-blue-400 flex-shrink-0">
                        <FaEnvelope />
                      </div>
                      <div>
                        <p className="text-xs text-slate-400 uppercase font-bold tracking-wider mb-1">Email Us</p>
                        <a href="mailto:academic-counsellor@vidyastu.in" className="hover:text-blue-400 transition-colors">academic-counsellor@vidyastu.in</a>
                      </div>
                    </div>

                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center text-blue-400 flex-shrink-0">
                        <FaMapMarkerAlt />
                      </div>
                      <div>
                        <p className="text-xs text-slate-400 uppercase font-bold tracking-wider mb-1">Visit HQ</p>
                        <p className="text-sm leading-relaxed text-slate-300">
                          74, Rastraguru Ave, Clive House,<br />
                          Dumdum (Near Nagerbazar),<br />
                          Kolkata, West Bengal 700028
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mt-12">
                  <p className="text-sm text-slate-400 mb-4">Connect with us:</p>
                  <div className="flex gap-3">
                    {socialLinks.map((social, idx) => (
                      <Link 
                        key={idx} 
                        href={social.href} 
                        target="_blank"
                        className={`w-10 h-10 rounded-full bg-slate-700 flex items-center justify-center text-white transition-all duration-300 ${social.color}`}
                      >
                        <social.icon size={16} />
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Right: Contact Form (Light Theme) */}
            <div className="lg:w-7/12 p-10 md:p-12 bg-white">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="group">
                    <label className="block text-xs font-bold text-slate-500 uppercase mb-2 group-focus-within:text-blue-600">First Name</label>
                    <input
                      type="text"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleChange}
                      className="w-full border-b-2 border-slate-200 bg-slate-50/50 px-3 py-3 outline-none focus:border-blue-600 transition-colors rounded-t-lg"
                      placeholder="John"
                    />
                  </div>
                  <div className="group">
                    <label className="block text-xs font-bold text-slate-500 uppercase mb-2 group-focus-within:text-blue-600">Last Name</label>
                    <input
                      type="text"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleChange}
                      className="w-full border-b-2 border-slate-200 bg-slate-50/50 px-3 py-3 outline-none focus:border-blue-600 transition-colors rounded-t-lg"
                      placeholder="Doe"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="group">
                    <label className="block text-xs font-bold text-slate-500 uppercase mb-2 group-focus-within:text-blue-600">Email Address</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className="w-full border-b-2 border-slate-200 bg-slate-50/50 px-3 py-3 outline-none focus:border-blue-600 transition-colors rounded-t-lg"
                      placeholder="john@example.com"
                    />
                  </div>
                  <div className="group">
                    <label className="block text-xs font-bold text-slate-500 uppercase mb-2 group-focus-within:text-blue-600">Phone Number</label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="w-full border-b-2 border-slate-200 bg-slate-50/50 px-3 py-3 outline-none focus:border-blue-600 transition-colors rounded-t-lg"
                      placeholder="+91 98765 43210"
                    />
                  </div>
                </div>

                <div className="group">
                  <label className="block text-xs font-bold text-slate-500 uppercase mb-2 group-focus-within:text-blue-600">Subject</label>
                  <div className="space-y-2">
                    {["Course Inquiry", "Technical Support", "General Question", "Partnership"].map((opt) => (
                      <label key={opt} className="inline-flex items-center mr-6 cursor-pointer">
                        <input
                          type="radio"
                          name="subject"
                          value={opt}
                          checked={formData.subject === opt}
                          onChange={handleChange}
                          className="form-radio text-blue-600 focus:ring-blue-500 h-4 w-4"
                        />
                        <span className="ml-2 text-sm text-slate-600">{opt}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div className="group">
                  <label className="block text-xs font-bold text-slate-500 uppercase mb-2 group-focus-within:text-blue-600">Message</label>
                  <textarea
                    name="message"
                    rows="4"
                    value={formData.message}
                    onChange={handleChange}
                    className="w-full border-2 border-slate-200 bg-slate-50/50 px-4 py-3 outline-none focus:border-blue-600 transition-colors rounded-xl resize-none"
                    placeholder="Tell us how we can help you..."
                  ></textarea>
                </div>

                <div className="flex justify-end pt-4">
                  <button
                    type="submit"
                    disabled={disable}
                    className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-bold py-4 px-8 rounded-xl shadow-lg shadow-blue-600/30 transform transition-all hover:-translate-y-1 disabled:opacity-70 disabled:cursor-not-allowed disabled:hover:translate-y-0"
                  >
                    {disable ? (
                      "Sending..."
                    ) : (
                      <>
                        Send Message <FaPaperPlane size={14} />
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>

        {/* --- Map Section --- */}
        <div className="w-full h-[450px] relative grayscale hover:grayscale-0 transition-all duration-700">
          <iframe
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3682.730398522733!2d88.41310387511618!3d22.626540679453377!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x39f89e10d12097a7%3A0xa74122e5f4a78073!2s74%2C%20Rastraguru%20Ave%2C%20Clive%20House%2C%20Ramgarh%2C%20Kolkata%2C%20West%20Bengal%20700028!5e0!3m2!1sen!2sin!4v1719408263747!5m2!1sen!2sin"
            className="w-full h-full border-0"
            allowFullScreen=""
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          ></iframe>
          {/* Overlay to merge map with footer visually */}
          <div className="absolute bottom-0 left-0 w-full h-24 bg-gradient-to-t from-slate-50 to-transparent pointer-events-none"></div>
        </div>

        <WhatsAppButton />
      </main>
    </>
  );
};

export default ContactPage;